import { Download, FileSearch, Store } from "lucide-react";
import { Link } from "react-router-dom";
import { Explainer } from "../components/Explainer";
import { Pill } from "../components/Pill";
import { ExportButtons } from "../components/register/ExportButtons";
import { assetExports, assetPath, overviewExport } from "../components/register/exports";
import type { Job, UC5Job } from "../lib/api";
import { listNote, type ListStatus } from "../components/States";
import { TableScroll } from "../components/TableScroll";
import { vendorStageLabel } from "../lib/words";

/** Completed work, collected in one place. Read from the same jobs the modules produce. */
export function Saved({ jobs, uc5Jobs, status = "ready", uc5Status = "ready", onOpen }: {
  jobs: Job[]; uc5Jobs: UC5Job[]; onOpen: (j: Job) => void;
  /** Whether each list has answered; "Nothing saved yet" is said only once it has. */
  status?: ListStatus; uc5Status?: ListStatus;
}) {
  const doneNote = listNote(status, 0, "Nothing saved yet.", "Loading extractions");
  const resolvedNote = listNote(uc5Status, 0, "Nothing saved yet.", "Loading vendor checks");
  const done = jobs.filter((j) => ["completed", "awaiting_review"].includes(j.status));
  const resolved = uc5Jobs.filter((j) => j.spec_approved_by);
  const packages = [...new Set(done.map((j) => j.package))].sort();

  return (
    <div className="page">
      <h2 style={{ fontSize: 26, marginBottom: 6 }}>Saved</h2>
      <p className="muted" style={{ marginBottom: 24 }}>
        Extractions and resolutions that have reached a person.
      </p>

      <div className="tiles" style={{ marginBottom: 28 }}>
        <div className="card tile"><div className="l">Extractions</div><div className="n">{done.length}</div></div>
        <div className="card tile"><div className="l">Resolutions</div><div className="n">{resolved.length}</div></div>
      </div>

      <h3 style={{ fontSize: 18, marginBottom: 12 }}>
        <span className="row"><FileSearch size={18} /> Identity extractions</span>
      </h3>
      <section className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 28 }}>
        <TableScroll label="Saved extractions">
        <table>
          <thead><tr><th>Job</th><th>Tag</th><th>Package</th><th>Status</th><th>Downloads</th><th /></tr></thead>
          <tbody>
            {done.length === 0 && <tr><td colSpan={6} className="muted">{doneNote}</td></tr>}
            {done.map((j) => (
              <tr key={j.id}>
                <td className="mono"><Link to={`/extraction/${encodeURIComponent(j.id)}`} title="Open this extraction">J-{j.id.slice(0, 4).toUpperCase()}</Link></td>
                <td><Link className="mono" to={assetPath(j.package, j.tag)} title="Register entry for this tag">{j.tag}</Link></td>
                <td>{j.package}</td><td><Pill value={j.status} /></td>
                <td><ExportButtons label={`Downloads for ${j.tag}`} links={assetExports(j.package, j.tag)} /></td>
                <td><button className="ghost" onClick={() => onOpen(j)}>Open</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      </section>

      <h3 style={{ fontSize: 18, marginBottom: 12 }}>
        <span className="row"><Store size={18} /> Successor resolutions</span>
      </h3>
      <section className="card" style={{ padding: 0, overflow: "hidden" }}>
        <TableScroll label="Saved vendor checks">
        <table>
          <thead><tr><th>Check</th><th>Model</th><th>Approved by</th><th>Stage</th><th /></tr></thead>
          <tbody>
            {resolved.length === 0 && <tr><td colSpan={5} className="muted">{resolvedNote}</td></tr>}
            {resolved.map((j) => (
              <tr key={j.id}>
                <td className="mono"><Link to={`/vendor/${encodeURIComponent(j.id)}`} title="Open this vendor check">U-{j.id.slice(0, 4).toUpperCase()}</Link></td>
                <td className="mono">{j.identity.model_number}</td>
                <td>{j.spec_approved_by}</td><td>{vendorStageLabel(j.stage)}</td>
                <td><Link to={`/vendor/${encodeURIComponent(j.id)}`}>Open</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      </section>

      <h3 style={{ fontSize: 18, margin: "28px 0 12px" }}>
        <span className="row"><Download size={18} /> Package overviews</span>
      </h3>
      {packages.length === 0 ? <p className="muted">{status === "ready" ? "A package overview appears once a tag in it is saved." : doneNote}</p> : (
        <section className="card" style={{ padding: 0, overflow: "hidden" }}>
          <TableScroll label="Package overviews">
          <table>
            <thead><tr><th>Package</th><th>Download</th></tr></thead>
            <tbody>
              {packages.map((p) => (
                <tr key={p}><td>{p}</td><td><ExportButtons label={`Overview for ${p}`} links={[overviewExport(p)]} /></td></tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
        </section>
      )}

      <div style={{ marginTop: 24 }}>
        <Explainer>
          <span className="row"><Download size={14} /> Downloads are built from the asset register, with the
          document and page beside every value. A tag the register does not hold yet says so when you download.</span>
        </Explainer>
      </div>
    </div>
  );
}
